import { mapValues } from 'lodash';
import { mapAxiosResponse } from '../../shared/utils/api-utils';
import { ChatMessage } from '../../shared/models/chat';
import { Job } from '../../shared/models/jobs';
import { GLYMPSE_PRO_CHAT_ORIGIN } from '../../shared/constants/chat.constants';
import ErcApiClient from '../../shared/services/erc-api-client';
import EnrouteApiClient from '../../shared/services/enroute-api-client';
import CoreApiClient from '../../shared/services/core-api-client';
import { ServerError, ServerErrorMessages } from '../../shared/utils/server-error-utils';

// Normalizes any thrown error (a raw AxiosError from a failed HTTP request,
// or an Error thrown by mapAxiosResponse for a {result: "failure"} envelope)
// into a plain string message, so it's always safe to render in the UI.
const toErrorMessage = (error: any): string => ServerErrorMessages[error?.message] ?? ServerErrorMessages[ServerError.UnexpectedError];

interface RawChatMessage {
  id: number;
  author?: string;
  contents: string;
  sequence_id: number;
  created_time: number;
}

interface RawChatChannel {
  id: number;
  messages: RawChatMessage[];
}

// The bulk-messages endpoint doesn't tag messages with an origin: a message
// from the customer carries an `author`, one from us doesn't.
const mapRawMessage = (message: RawChatMessage): ChatMessage => ({
  ...message,
  origin: message.author ? 'customer' : GLYMPSE_PRO_CHAT_ORIGIN,
});

export default class ChatService {
  static async getChatChannels(orgId: number, agentId?: number): Promise<Job[]> {
    const resp = await EnrouteApiClient.get(`/org/${orgId}/tasks`, {
      params: { state: 'not_completed', children: true, ...(agentId ? { agent_id: agentId } : {}) },
    });
    const tasks = mapAxiosResponse<Job[]>(resp);
    return tasks.filter((task) => task?.chat_channel);
  }

  static async getMessagesForChannels(chatChannels: string[]): Promise<Record<string, ChatMessage[]>> {
    if (chatChannels.length === 0) {
      return {};
    }
    const resp = await CoreApiClient.get('/chat/channels', {
      params: { channel_ids: chatChannels.join(',') },
    });
    const channels = mapAxiosResponse<Record<string, RawChatChannel>>(resp);
    return mapValues(channels, (channel) => (channel.messages ?? []).map(mapRawMessage));
  }

  static async loadMessages(chatChannel: string): Promise<ChatMessage[]> {
    return ErcApiClient.get(`/curbside/messages/${chatChannel}`)
      .then((resp) => mapAxiosResponse<ChatMessage[]>(resp))
      .catch((error) => {
        throw new Error(toErrorMessage(error));
      });
  }

  static async sendMessage(chatChannel: string, message: ChatMessage, inviteCode: string): Promise<ChatMessage> {
    return ErcApiClient.post(`/curbside/messages/${chatChannel}`, { message, inviteCode, type: 'delivery' })
      .then((resp) => mapAxiosResponse<ChatMessage>(resp))
      .catch((error) => {
        throw new Error(toErrorMessage(error));
      });
  }
}
